import React, { useState, useEffect, useCallback } from 'react';
import { WhitelistAPI, PublicAPI } from '../services/portalApi';
import { PortalConfirmModal } from '../components/PortalConfirmModal';

interface WhitelistEntry {
  id: string;
  ip_address: string;
  label: string | null;
  added_by: string;
  created_at: string;
  expires_at: string | null;
}

export const PortalDashboard: React.FC = () => {
  const [entries, setEntries] = useState<WhitelistEntry[]>([]);
  const [myIp, setMyIp] = useState('');
  const [newIp, setNewIp] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{ id: string; ip: string } | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [wl, ip] = await Promise.all([WhitelistAPI.list(), PublicAPI.getMyIp()]);
      setEntries(wl.data);
      setMyIp(ip.data.ip);
    } catch {
      setError('Failed to load data');
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await WhitelistAPI.add({ ip_address: newIp, label: newLabel || undefined });
      setNewIp('');
      setNewLabel('');
      setSuccess(`IP ${newIp} whitelisted successfully`);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to add IP');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id: string, ip: string) => {
    setConfirmTarget({ id, ip });
  };

  const confirmRemove = async () => {
    if (!confirmTarget) return;
    setConfirmTarget(null);
    setError('');
    setSuccess('');
    try {
      await WhitelistAPI.remove(confirmTarget.id);
      setSuccess(`IP ${confirmTarget.ip} removed`);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to remove IP');
    }
  };

  const handleAddMyIp = () => {
    setNewIp(myIp);
    setNewLabel('My Current IP');
  };

  const copyIp = () => {
    navigator.clipboard.writeText(myIp);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isMyIpWhitelisted = entries.some((e) => e.ip_address === myIp);

  return (
    <div>
      <div className="portal-flex-between portal-mb-24">
        <h2 style={{ margin: 0 }}>IP Whitelist Dashboard</h2>
        <div className="portal-your-ip">
          Your IP: <code>{myIp || '...'}</code>
          <button className="portal-copy-btn" onClick={copyIp}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
          {myIp && !isMyIpWhitelisted && (
            <button className="portal-btn portal-btn-primary portal-btn-sm" onClick={handleAddMyIp}>
              + Whitelist My IP
            </button>
          )}
          {isMyIpWhitelisted && <span className="portal-badge portal-badge-green">Whitelisted</span>}
        </div>
      </div>

      {error && <div className="portal-alert portal-alert-error">{error}</div>}
      {success && <div className="portal-alert portal-alert-success">{success}</div>}

      {/* Add IP Form */}
      <div className="portal-card">
        <h3>Add IP Address</h3>
        <div className="portal-flex portal-gap-8" style={{ alignItems: 'flex-end' }}>
          <div className="portal-form-group" style={{ flex: 2, marginBottom: 0 }}>
            <label>IP Address</label>
            <input
              className="portal-input"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              placeholder="e.g. 203.0.113.50"
            />
          </div>
          <div className="portal-form-group" style={{ flex: 2, marginBottom: 0 }}>
            <label>Label (optional)</label>
            <input
              className="portal-input"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Office Network"
            />
          </div>
          <button
            className="portal-btn portal-btn-primary"
            onClick={handleAdd}
            disabled={!newIp || loading}
            style={{ marginBottom: 0 }}
          >
            {loading ? 'Adding...' : 'Add IP'}
          </button>
        </div>
      </div>

      {/* Whitelist Table */}
      <div className="portal-card">
        <h3>Whitelisted IPs ({entries.length})</h3>
        {entries.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            No IPs whitelisted yet. All traffic is currently allowed.
          </p>
        ) : (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>IP Address</th>
                  <th>Label</th>
                  <th>Added</th>
                  <th>Expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <code style={{ fontWeight: 600 }}>{e.ip_address}</code>
                      {e.ip_address === myIp && (
                        <span className="portal-badge portal-badge-blue" style={{ marginLeft: 8 }}>You</span>
                      )}
                    </td>
                    <td>{e.label || '—'}</td>
                    <td>{new Date(e.created_at).toLocaleDateString()}</td>
                    <td>{e.expires_at ? new Date(e.expires_at).toLocaleDateString() : 'Never'}</td>
                    <td>
                      <button
                        className="portal-btn portal-btn-danger portal-btn-sm"
                        onClick={() => handleRemove(e.id, e.ip_address)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PortalConfirmModal
        open={!!confirmTarget}
        title="Remove IP"
        message={`Remove ${confirmTarget?.ip} from the whitelist?`}
        confirmLabel="Remove"
        danger
        onConfirm={confirmRemove}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
};
